import { useMemo } from "react";
import {
  CartesianGrid,
  ReferenceLine,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  Tooltip,
  XAxis,
  YAxis,
  ZAxis,
} from "recharts";

import { RangoDeEje } from "@/components/referencias/RangoDeEje";
import { RotuloDeFiltro } from "@/components/RotuloDeFiltro";
import { useValorSostenido } from "@/hooks/useValorSostenido";
import { numero } from "@/lib/formatoDeMercado";
import { agruparPorGrilla } from "@/lib/agrupacionDePuntos";
import {
  claveDeComparable,
  type Comparable,
  type PuntoDelCatalogo,
} from "@/lib/referencias";
import { cn } from "@/lib/utils";

interface ScatterValorSuperficieProps {
  fichas: Comparable[];
  seleccionadas: Set<string>;
  onAlternar: (ficha: Comparable) => void;
  medianaUfHa: number | null;
  /**
   * TODO el filtro, no solo la página cargada.
   *
   * Es lo que le da FORMA a la nube: con seis puntos no se ve si los predios
   * chicos se pagan más por hectárea ni dónde está el grueso del mercado, que
   * es justo para lo que existe este gráfico.
   */
  mercado?: PuntoDelCatalogo[];
  /** El filtro del eje X. Sin él, el gráfico solo se mira. */
  superficie?: ControlDeEje;
  /** El filtro del eje Y. */
  valorUnitario?: ControlDeEje;
}

/**
 * Un filtro de rango que vive PEGADO A SU EJE.
 *
 * `escala` es hasta dónde llega la PISTA y NO lo que el gráfico dibuja: es la
 * medida estable del mercado —la banda sembrada en superficie, la mediana por
 * cuatro en valor unitario—, la misma que ya usaba el control cuando estaba en
 * la barra. Sacarla del conjunto ya acotado la encogería bajo la manija y el
 * filtro se apretaría solo.
 */
export interface ControlDeEje {
  /** Los dos extremos elegidos. */
  valor: [number, number];
  /** El tope de la PISTA, no el del dibujo. */
  escala: number;
  onCambio: (valor: number[]) => void;
  paso?: number;
  deshabilitado?: boolean;
}

/* LA GEOMETRÍA QUE COMPARTEN EL GRÁFICO Y SUS DOS PISTAS.

   El ancho del eje vertical NO está acá: lo calcula `anchoDelEje` del tick más
   largo, así que la pista horizontal lo lee de ahí. Fijarlo como constante la
   desalinearía en cuanto el eje pidiera un dígito más.

   Va en PÍXELES, y es la excepción correcta a la regla del DS: recharts dibuja
   su SVG en píxeles absolutos, así que una alineación escrita en `rem` se
   correría del eje en cuanto se moviera la perilla de densidad. Y va en UN solo
   lugar porque son dos piezas que tienen que coincidir: una pista que no
   arranca donde arranca su eje convierte el control en un adorno que miente. */
const MARGEN = { top: 8, right: 12, bottom: 0, left: 0 } as const;
/** Lo que el eje horizontal reserva para los suyos. */
const ALTO_EJE_X = 24;
/** El ancho de la columna de la pista vertical más su separación del gráfico. */
const COLUMNA_PISTA_Y = "1.5rem";

/** Un punto de la nube de fondo: una celda de la grilla, con cuántos hay. */
interface PuntoDeMercado {
  x: number;
  y: number;
  cantidad: number;
}

interface Punto {
  x: number;
  y: number;
  ficha: Comparable;
  seleccionada: boolean;
}

/** Separa una ficha cargada de una celda de la nube dentro del mismo globo. */
function esPuntoDeFicha(punto: Punto | PuntoDeMercado): punto is Punto {
  return "ficha" in punto;
}

/**
 * Qué tramo del eje se dibuja: la banda si está puesta, los datos si no.
 *
 * "Abierta" es la manija en el extremo, y se mira contra la ESCALA y no contra
 * el dibujo: es lo único que distingue "no estoy filtrando" de "filtré justo
 * hasta donde llega el mercado".
 */
function encuadre(
  valor: number[],
  control: ControlDeEje | undefined,
  dominioDeLosDatos: [number, number] | undefined,
): [number, number] | undefined {
  return bandaPuesta(valor, control) ? [valor[0], valor[1]] : dominioDeLosDatos;
}

/**
 * ¿La banda acota algo, o abarca su escala entera?
 *
 * Se mira contra la ESCALA y no contra lo que el gráfico dibuja: es lo único
 * que distingue "no estoy filtrando" de "filtré justo hasta donde llega el
 * mercado". Y decide dos cosas a la vez —el encuadre del eje y si la lectura va
 * en tinta plena— porque son la misma pregunta.
 */
export function bandaPuesta(valor: number[], control: ControlDeEje | undefined): boolean {
  if (!control || control.deshabilitado || valor.length < 2) return false;

  return valor[0] > 0 || valor[1] < control.escala;
}

/**
 * Valor unitario contra superficie: dónde cae cada comparable en el mercado.
 *
 * PARA QUÉ SIRVE, que es lo que decide su forma. Una tabla ordenada por UF/ha
 * dice cuál es el más caro; este gráfico dice si el conjunto tiene FORMA — si
 * los predios chicos se pagan más por hectárea (que es lo normal), si hay un
 * punto suelto arriba que va a torcer la mediana, si el predio tasado cae
 * dentro de la nube o en su borde. Eso no se lee en una lista.
 *
 * SOLO ENTRAN LAS FICHAS CON VALOR UNITARIO CALCULABLE, y la cuenta lo dice
 * ("6 de 9 puntos"). Un aviso sin precio no vale cero: dibujarlo en el eje
 * inferior inventaría un dato barato que no existe, y justo abajo, que es
 * donde más llama la atención.
 *
 * LA LÍNEA ES LA MEDIANA DEL FILTRO, y acá hay un desvío del prototipo: su
 * bajada dice "el rango en que cae el predio tasado" pero la línea que dibuja
 * está rotulada "MEDIANA DEL FILTRO". Se resolvió por la mediana porque es lo
 * que el sistema SÍ calcula y es contra lo que compara la barra de cada ficha:
 * dos referencias distintas en la misma pantalla se contradirían.
 */
export function ScatterValorSuperficie({
  fichas,
  seleccionadas,
  onAlternar,
  medianaUfHa,
  mercado = [],
  superficie,
  valorUnitario,
}: ScatterValorSuperficieProps) {
  const puntos = useMemo<Punto[]>(
    () =>
      fichas
        .filter(
          (f) =>
            f.uf_ha_terreno !== null &&
            f.uf_ha_terreno > 0 &&
            f.superficie_terreno_m2 !== null &&
            f.superficie_terreno_m2 > 0,
        )
        .map((f) => ({
          x: (f.superficie_terreno_m2 as number) / 10_000,
          y: f.uf_ha_terreno as number,
          ficha: f,
          seleccionada: seleccionadas.has(claveDeComparable(f)),
        })),
    [fichas, seleccionadas],
  );

  const nube = useMemo(() => agruparElMercado(mercado), [mercado]);

  /**
   * El eje Y se ajusta a los datos, y es lo que vuelve visible el filtro de
   * valor unitario.
   *
   * Recharts ancla un eje numérico en CERO por defecto. Con la banda acotada a
   * 800 – 1.200 UF/ha, el eje seguía yendo de 0 a 1.200 y la nube quedaba
   * aplastada contra el borde de arriba: el filtro parecía no haber hecho nada,
   * que es justo lo contrario de para lo que se movió.
   *
   * Entra la nube del mercado además de las fichas cargadas. Son las dos capas
   * que se dibujan, y un eje calculado sobre una sola dejaría a la otra fuera
   * del área — puntos que existen y no se ven.
   */
  const escalaY = useMemo(
    () => escalaVertical([...puntos.map((p) => p.y), ...nube.map((c) => c.y)]),
    [puntos, nube],
  );
  const escalaX = useMemo(
    () => escalaVertical([...puntos.map((p) => p.x), ...nube.map((c) => c.x)]),
    [puntos, nube],
  );

  /* EL VALOR EN VUELO DE CADA PISTA, para que la lectura de la cabecera siga a
     la manija mientras se arrastra. Vive acá y no en `RangoDeEje` porque el
     número no se dibuja junto al control: en una columna de 1 rem no cabe. */
  const ejeX = useValorSostenido(superficie?.valor ?? []);
  const ejeY = useValorSostenido(valorUnitario?.valor ?? []);

  /* EL ENCUADRE SIGUE A LA BANDA: acotar hace ZOOM sobre lo que quedó.

     Con la banda puesta, los bordes del eje son EXACTAMENTE los dos números de
     las manijas — que es lo que vuelve honesto tener el control pegado al eje.
     Con la banda abierta no hay nada que respetar y manda el dominio de los
     datos, que es lo que ya hacía el gráfico.

     La alternativa era dibujar siempre la escala entera para que la manija
     cayera físicamente sobre su marca del eje, y se descartó con evidencia del
     propio repo: recharts ancla un eje numérico en cero, y una banda alta
     —800 a 1.200 UF/ha sobre una escala que llega a 9.000— deja la nube
     aplastada contra un borde, que es el defecto que `dominioVertical` vino a
     cerrar. El contexto que se pierde así no se pierde: lo dice la PISTA, cuyo
     relleno es la porción del mercado que se está viendo. */
  /* Sin ninguna pista el gráfico no tiene nada que ofrecer cuando queda
     vacío, así que ahí sí cede la tarjeta entera al aviso. */
  const hayPistas = Boolean(superficie || valorUnitario);

  const dominioX = encuadre(ejeX.mostrado, superficie, escalaX?.dominio);
  const dominioY = encuadre(ejeY.mostrado, valorUnitario, escalaY?.dominio);

  /* LAS MARCAS SOLO VALEN PARA SU PROPIO DOMINIO. `escalaVertical` las derivó
     del rango de los DATOS; con la banda puesta el eje dibuja otro tramo, así
     que esas marcas caerían fuera —o se apelotonarían en un borde— y recharts
     las reparte solo. */
  const marcasX = bandaPuesta(ejeX.mostrado, superficie) ? undefined : escalaX?.ticks;
  const marcasY = bandaPuesta(ejeY.mostrado, valorUnitario) ? undefined : escalaY?.ticks;

  /**
   * El ancho del eje, medido sobre el rótulo MÁS LARGO que va a imprimir.
   *
   * Era un `52` fijo, y por eso "133143.9492" se salía de la tarjeta. Un número
   * fijo no puede servir: el mismo gráfico muestra "0,6" en un filtro y
   * "200.000" en otro, y el que entra en un caso desperdicia media pulgada en el
   * otro. Recharts 2 no tiene `width="auto"` —llegó en la 3—, así que se estima.
   *
   * `7` px por carácter a 11 px de tipografía es generoso a propósito: un dígito
   * mide ~6,1 y el punto de miles bastante menos, así que el error sobra hacia
   * el lado que no corta. Los 22 px son el rótulo "UF / ha" girado, que vive
   * DENTRO del ancho del eje.
   */
  const anchoDelEje = useMemo(() => {
    if (escalaY === undefined) return 52;

    const masLargo = escalaY.ticks.reduce(
      (largo, tick) => Math.max(largo, numero(tick, escalaY.decimales).length),
      0,
    );

    return Math.min(Math.max(masLargo * 7 + 22, 52), 96);
  }, [escalaY]);

  return (
    <section
      aria-label="Valor unitario vs. superficie"
      className="border-border flex flex-col rounded-xl border-[1.5px] p-4"
    >
      <div className="flex items-baseline justify-between gap-3">
        <h3 className="text-ink text-sm font-bold">Valor unitario vs. superficie</h3>
        <span className="text-ink-hint font-mono text-xs tabular-nums">
          {puntos.length === fichas.length
            ? `${puntos.length} puntos`
            : `${puntos.length} de ${fichas.length} puntos`}
        </span>
      </div>

      <p className="text-ink-hint text-caption mt-0.5">
        {medianaUfHa === null
          ? "Clic en un punto para seleccionarlo."
          : "La línea marca la mediana del filtro. Clic en un punto para seleccionarlo."}
      </p>

      {/* LAS DOS LECTURAS, ARRIBA Y JUNTAS.

          No van pegadas a su pista: la del eje Y tendría que caber en una
          columna de 1 rem, y girada noventa grados es justo el número que hay
          que leer de un vistazo mientras se arrastra. Acá comparten la línea
          donde el ojo ya está, y se mueven con la manija porque el valor en
          vuelo lo sostiene este componente. */}
      {(superficie || valorUnitario) && (
        <div className="mt-2 flex flex-wrap items-baseline gap-x-4 gap-y-1">
          {superficie && (
            <Lectura
              rotulo="Superficie"
              texto={`${numero(ejeX.mostrado[0] ?? 0)} – ${numero(ejeX.mostrado[1] ?? 0)} ha`}
              acota={bandaPuesta(ejeX.mostrado, superficie)}
            />
          )}
          {valorUnitario && (
            <Lectura
              rotulo="Valor unitario"
              texto={
                valorUnitario.deshabilitado
                  ? "—"
                  : `${numero(ejeY.mostrado[0] ?? 0)} – ${numero(ejeY.mostrado[1] ?? 0)} UF/ha`
              }
              acota={bandaPuesta(ejeY.mostrado, valorUnitario)}
            />
          )}
        </div>
      )}

      {puntos.length === 0 && !hayPistas ? (
        <div className="flex flex-1 flex-col items-center justify-center gap-1 py-10 text-center">
          <p className="text-ink text-sm font-semibold">Sin puntos que graficar</p>
          <p className="text-ink-hint text-caption max-w-xs">
            Ningún comparable del filtro tiene valor unitario calculable.
          </p>
        </div>
      ) : (
        <>
          {/* EL GRÁFICO Y SUS DOS PISTAS.

              Las pistas se alinean con el ÁREA DE TRAZADO, no con la tarjeta:
              una manija que no arranca donde arranca su eje convierte el
              control en un adorno. Por eso los tres paddings salen de las
              mismas constantes que recibe el `ScatterChart`. */}
          {/* EL ALTO NO ES UN NÚMERO: LO PONE LA TARJETA DE AL LADO.

              El gráfico comparte fila con el mapa, y el mapa acaba de pasar a
              26 rem y de ganar el filtro de distancia debajo. Con un alto fijo
              acá habría que perseguir ese número cada vez que el mapa cambie —y
              el día que no coincidieran, dos tarjetas hermanas de distinto alto.
              Con el área elástica, la grilla estira las dos por igual y el
              dibujo se queda con lo que sobre.

              El piso es para cuando NO hay hermana que estire: abajo de `xl` el
              gráfico y el mapa se apilan, y ahí `flex-1` no reparte nada. Son
              las 26 rem del mapa —el mismo alto pedido, también apilados— y es
              el ÚNICO número que hay que perseguir si el mapa cambia:
              `MapaDeComparables` lo declara en su prop `alto`. */}
          <div className="mt-3 flex min-h-[26rem] flex-1 gap-2">
            {valorUnitario && (
              <div
                className="w-4 shrink-0"
                style={{
                  paddingTop: MARGEN.top,
                  paddingBottom: ALTO_EJE_X + MARGEN.bottom,
                }}
              >
                <RangoDeEje
                  rotulo="Valor unitario"
                  orientacion="vertical"
                  valor={ejeY.mostrado}
                  onArrastre={ejeY.elegir}
                  onCambio={valorUnitario.onCambio}
                  max={valorUnitario.escala || 1}
                  paso={valorUnitario.paso}
                  deshabilitado={valorUnitario.deshabilitado}
                  className="h-full"
                />
              </div>
            )}

            <div className="min-h-0 min-w-0 flex-1">
              {/* EL AVISO OCUPA EL ÁREA DEL DIBUJO Y NADA MÁS: LAS PISTAS SE
                  QUEDAN. Acotar hasta dejar cero puntos es un camino normal
                  —dos manijas alcanzan— y si los controles desaparecieran con
                  la nube, la única salida sería "Limpiar", que borra también
                  los otros cuatro filtros. Un filtro tiene que poder
                  deshacerse desde donde se puso. */}
              {puntos.length === 0 ? (
                <div className="flex h-full flex-col items-center justify-center gap-1 text-center">
                  <p className="text-ink text-sm font-semibold">Sin puntos que graficar</p>
                  <p className="text-ink-hint text-caption max-w-xs">
                    Ningún comparable del filtro cae dentro de estos dos rangos.
                  </p>
                </div>
              ) : (
              <ResponsiveContainer width="100%" height="100%">
                <ScatterChart margin={{ ...MARGEN }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />

                  <XAxis
                    type="number"
                    dataKey="x"
                    name="Superficie"
                    unit=" ha"
                    domain={dominioX}
                    ticks={marcasX}
                    tickFormatter={(valor: number) => numero(valor, escalaX?.decimales ?? 0)}
                    height={ALTO_EJE_X}
                    tick={{ fontSize: 11 }}
                    stroke="var(--color-ink-hint)"
                  />
                  {/* SIN `label`: el rótulo del eje subió a la cabecera, junto a
                      la lectura de su pista. Repetirlo acá diría dos veces lo
                      mismo y le comería ancho al dibujo. */}
                  <YAxis
                    type="number"
                    dataKey="y"
                    name="UF/ha"
                    domain={dominioY}
                    ticks={marcasY}
                    tickFormatter={(valor: number) => numero(valor, escalaY?.decimales ?? 0)}
                    tick={{ fontSize: 11 }}
                    stroke="var(--color-ink-hint)"
                    width={anchoDelEje}
                  />
                  {/* Un rango fijo en Z: sin él recharts escala el tamaño del
                      punto con el dato y las fichas chicas quedan invisibles. */}
                  <ZAxis range={[60, 60]} />

                  {medianaUfHa !== null && (
                    <ReferenceLine
                      y={medianaUfHa}
                      stroke="var(--color-ink)"
                      strokeWidth={1.5}
                      strokeDasharray="4 3"
                    />
                  )}

                  <Tooltip
                    cursor={{ strokeDasharray: "3 3" }}
                    content={<Globo />}
                    // `isAnimationActive` apagado en todo el gráfico: con la
                    // animación, un clic sobre un punto lo mueve antes de que el
                    // navegador registre el evento.
                    isAnimationActive={false}
                  />

                  {/* El mercado va PRIMERO para quedar DETRÁS: es el fondo
                      contra el que se leen las fichas cargadas, no un dato del
                      mismo peso. */}
                  {nube.length > 0 && (
                    <Scatter
                      data={nube}
                      isAnimationActive={false}
                      shape={<PuntoDeLaNube />}
                      // Sin puntero ni tooltip propio: no es accionable, porque
                      // una celda de la grilla no es una ficha que se pueda
                      // seleccionar.
                      legendType="none"
                    />
                  )}

                  <Scatter
                    data={puntos}
                    isAnimationActive={false}
                    shape={<PuntoDelGrafico />}
                    onClick={(dato: unknown) => {
                      const punto = dato as { ficha?: Comparable };
                      if (punto?.ficha) onAlternar(punto.ficha);
                    }}
                    className="cursor-pointer"
                  />
                </ScatterChart>
              </ResponsiveContainer>
              )}
            </div>
          </div>

          {superficie && (
            /* EL AIRE CONTRA EL EJE NO ES DECORACIÓN. La pista quedaba pegada a
               los números del eje —"0 ha", "7 ha"— y las dos filas se leían como
               una sola escala continua, que es justo lo que NO son: el eje dibuja
               el tramo elegido y la pista abarca todo el mercado. Separadas, cada
               una vuelve a decir lo suyo. */
            <div
              className="mt-4"
              style={{
                marginLeft: valorUnitario
                  ? `calc(${COLUMNA_PISTA_Y} + ${anchoDelEje + MARGEN.left}px)`
                  : anchoDelEje + MARGEN.left,
                marginRight: MARGEN.right,
              }}
            >
              <RangoDeEje
                rotulo="Superficie"
                orientacion="horizontal"
                valor={ejeX.mostrado}
                onArrastre={ejeX.elegir}
                onCambio={superficie.onCambio}
                max={superficie.escala || 1}
                paso={superficie.paso}
              />

              {/* LOS EXTREMOS DE LA PISTA, que NO son los del eje.

                  Hacen falta justamente porque no coinciden: el eje dibuja el
                  tramo elegido y la pista abarca todo el mercado, así que sin
                  estos dos números nada diría cuánto quedó afuera — y la pista
                  se leería como una segunda copia del eje. Van solo en la
                  horizontal: en la columna de 1 rem de la vertical no entran, y
                  ahí el tramo relleno ya alcanza para ver la proporción. */}
              <div className="text-ink-ghost mt-1 flex justify-between text-xs">
                <span>0</span>
                <span>{numero(superficie.escala)} ha</span>
              </div>
            </div>
          )}

          {puntos.length > 0 && <Leyenda />}
        </>
      )}
    </section>
  );
}

/**
 * El punto: rombo para una transacción, círculo para una oferta.
 *
 * La FORMA distingue la clase y el COLOR el estado, y no al revés. Un tasador
 * mira este gráfico para separar precios pagados de precios pedidos, que es la
 * distinción que más pesa; si eso fuera color, se perdería al imprimir en
 * blanco y negro y para quien no distingue rojo de verde.
 */
function PuntoDelGrafico(props: unknown) {
  const { cx, cy, payload } = props as {
    cx?: number;
    cy?: number;
    payload?: Punto;
  };

  if (cx === undefined || cy === undefined || !payload) return null;

  const esTransaccion = payload.ficha.clase === "transaccion";
  const lado = payload.seleccionada ? 7 : 5;

  return (
    <g>
      {payload.seleccionada && (
        <circle cx={cx} cy={cy} r={11} fill="var(--color-primary)" fillOpacity={0.22} />
      )}

      {esTransaccion ? (
        <rect
          x={cx - lado}
          y={cy - lado}
          width={lado * 2}
          height={lado * 2}
          transform={`rotate(45 ${cx} ${cy})`}
          fill={payload.seleccionada ? "var(--color-primary)" : "var(--color-info-dot)"}
          stroke="var(--color-background)"
          strokeWidth={1}
        />
      ) : (
        <circle
          cx={cx}
          cy={cy}
          r={lado}
          fill={payload.seleccionada ? "var(--color-primary)" : "var(--color-background)"}
          stroke={payload.seleccionada ? "var(--color-primary)" : "var(--color-warning-dot)"}
          strokeWidth={1.75}
        />
      )}
    </g>
  );
}

/**
 * El globo del gráfico, que ATIENDE A LAS DOS CAPAS y solo habla de una.
 *
 * Recharts declara el `<Tooltip>` a nivel del GRÁFICO y no por serie, así que
 * este mismo globo recibe tanto una ficha cargada como una celda de la nube de
 * fondo. Una celda no es una ficha: agrupa a muchas, no tiene título y no se
 * puede seleccionar. Por eso se busca la entrada que traiga `ficha` en vez de
 * dar por hecho que la primera lo es — sobre la nube el globo se calla, que es
 * lo mismo que ya declara su capa al no ser accionable.
 *
 * Se exporta para poder probarlo: `ResponsiveContainer` mide su contenedor y en
 * la suite todo mide cero, así que el SVG no llega a dibujarse y no hay ningún
 * punto que sobrevolar.
 */
export function Globo({
  active,
  payload,
}: {
  active?: boolean;
  payload?: { payload: Punto | PuntoDeMercado }[];
}) {
  const punto = payload?.map((entrada) => entrada.payload).find(esPuntoDeFicha);

  if (!active || !punto) return null;

  const { ficha, x, y } = punto;

  return (
    <div className="border-border bg-background rounded-md border px-2.5 py-1.5 shadow-md">
      <p className="text-ink max-w-[16rem] truncate text-xs font-semibold">{ficha.titulo}</p>
      <p className="text-ink-muted font-mono text-[0.6875rem] tabular-nums">
        {numero(x, 2)} ha · {numero(y, 0)} UF/ha
      </p>
    </div>
  );
}

/**
 * El rótulo de un eje y lo que su pista tiene puesto.
 *
 * EN TINTA PLENA SOLO CUANDO ACOTA, misma regla que ya trae `CampoDeRango`: con
 * dos cifras en negrita seguidas no se ve cuál de los dos ejes está filtrando
 * sin comparar cada número contra su escala.
 */
function Lectura({
  rotulo,
  texto,
  acota,
}: {
  rotulo: string;
  texto: string;
  acota: boolean;
}) {
  return (
    <span className="flex items-baseline gap-1.5">
      <RotuloDeFiltro>{rotulo}</RotuloDeFiltro>
      <span
        className={cn(
          "font-mono text-xs font-extrabold tabular-nums",
          acota ? "text-ink" : "text-ink-hint",
        )}
      >
        {texto}
      </span>
    </span>
  );
}

function Leyenda() {
  return (
    <ul className="text-ink-hint mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-[0.6875rem]">
      <Marca forma="rombo" clase="bg-info-dot" rotulo="Transacción CBR" />
      <Marca forma="circulo" clase="border-warning-dot" rotulo="Oferta publicada" />
      <Marca forma="rombo" clase="bg-primary" rotulo="Seleccionada" />
      <li className="flex items-center gap-1.5">
        <span className="border-ink w-3 border-t-[1.5px] border-dashed" />
        Mediana del filtro
      </li>
    </ul>
  );
}

function Marca({
  forma,
  clase,
  rotulo,
}: {
  forma: "rombo" | "circulo";
  clase: string;
  rotulo: string;
}) {
  return (
    <li className="flex items-center gap-1.5">
      <span
        className={cn(
          "size-2",
          forma === "rombo" ? "rotate-45" : "rounded-full border-[1.5px]",
          clase,
        )}
      />
      {rotulo}
    </li>
  );
}

/* ---------------------------------------------------------------------------
   La nube de fondo
   --------------------------------------------------------------------------- */

/**
 * Cuántas celdas tiene la grilla que resume el mercado.
 *
 * Es el tope de puntos que la nube puede dibujar (60 × 40 = 2.400) y en la
 * práctica quedan muchos menos, porque el mercado se concentra. Sin agrupar,
 * las 13.897 fichas del filtro serían 13.897 elementos SVG y el gráfico deja de
 * responder mucho antes de eso.
 */
const CELDAS_X = 60;
const CELDAS_Y = 40;

/**
 * Resume las fichas del filtro en una grilla sobre el propio gráfico.
 *
 * La grilla se arma sobre el RANGO de los datos y no sobre valores absolutos:
 * un filtro de parcelas de 300 m² y otro de fundos de 200 ha tienen que dar
 * nubes igual de legibles, y una celda de tamaño fijo dejaría una de las dos
 * apilada en una sola columna.
 */
/**
 * Cuántos tramos se busca partir el eje. No es cuántas marcas salen: el paso se
 * redondea a una cifra legible, así que quedan entre tres y seis.
 */
const TRAMOS_DEL_EJE = 4;

/**
 * El paso "lindo" más cercano a uno cualquiera: 1, 2, 5 o 10 por una potencia
 * de diez.
 *
 * Es el algoritmo de d3 (`tickIncrement`), y los umbrales son las medias
 * GEOMÉTRICAS entre los candidatos —√2, √10, √50— y no las aritméticas: en una
 * escala donde lo que importa son las proporciones, el punto medio entre 1 y 2
 * es 1,41 y no 1,5.
 */
function pasoLegible(bruto: number): number {
  const magnitud = 10 ** Math.floor(Math.log10(bruto));
  const error = bruto / magnitud;

  if (error >= Math.sqrt(50)) return 10 * magnitud;
  if (error >= Math.sqrt(10)) return 5 * magnitud;
  if (error >= Math.sqrt(2)) return 2 * magnitud;

  return magnitud;
}

const DECIMALES_MAXIMOS = 4;

/** Cuántos decimales necesita un número para escribirse entero. */
function decimalesDe(valor: number): number {
  const texto = String(valor);
  const punto = texto.indexOf(".");

  // Notación científica: son pasos absurdamente chicos para este eje, y el tope
  // los deja en el máximo que la pantalla sabe mostrar.
  if (texto.includes("e")) return DECIMALES_MAXIMOS;

  return punto === -1 ? 0 : Math.min(texto.length - punto - 1, DECIMALES_MAXIMOS);
}

/**
 * Los bordes del eje vertical, sus marcas y con cuántos decimales se escriben.
 *
 * EL DOMINIO NO SE LE DEJA A RECHARTS porque su default para un eje numérico es
 * anclar el piso en CERO, y eso vuelve invisible al filtro de valor unitario:
 * con la banda acotada a 800 – 1.200, el eje seguía yendo de 0 a 1.200 y la
 * nube quedaba aplastada contra el borde de arriba.
 *
 * El aire —un 6 % del alto a cada lado— existe para que un punto que caiga
 * justo en el extremo se dibuje entero y no cortado por el marco. Y el piso
 * nunca baja de cero: un valor unitario negativo no existe, y un eje que lo
 * ofreciera invitaría a leer la nube contra una referencia imposible.
 *
 * LAS MARCAS TAMPOCO, y ese fue el defecto del 08-09-2026. Con un dominio
 * explícito, recharts reparte sus bordes en partes iguales: sobre un filtro
 * urbano las marcas salían `33143.9492`, `83143.9492`, `133143.9492` y
 * `221053.8308`. Tres cosas mal a la vez.
 *
 *   1. Once caracteres no entran en el ancho del eje, así que los rótulos se
 *      salían de la tarjeta y quedaban cortados por la izquierda.
 *   2. Son cifras distintas en cada filtro. Una grilla sirve para ubicar un
 *      punto de un vistazo, y para eso las líneas tienen que caer en números
 *      que se reconozcan.
 *   3. La última marca era el BORDE del dominio, no un múltiplo del paso: el
 *      último tramo valía 88.000 donde los otros valían 50.000, dibujados a la
 *      misma altura. Dos franjas iguales significaban distinto.
 *
 * LAS MARCAS VAN DENTRO DEL DOMINIO, no al revés. Lo natural sería estirar el
 * dominio hasta el múltiplo de afuera —lo que hace `nice()` de d3—, pero acá
 * eso reintroduce el problema que este cálculo existe para evitar: con un paso
 * de 50.000, el piso se iría a cero y la nube volvería a aplastarse. Así que el
 * dominio lo fijan los DATOS y las marcas caen adentro, aunque la de más arriba
 * no toque el borde. Que la primera y la última no estén pegadas a los extremos
 * es lo normal en un gráfico, y no cuesta nada leerlo.
 *
 * LOS DECIMALES SALEN DEL PASO Y NO DEL TAMAÑO DE LAS CIFRAS. Redondear siempre
 * haría el efecto contrario al que se busca: con valores entre 0,5 y 0,9 las
 * marcas enteras serían "0" y "1" repetidas, y el eje dejaría de decir nada.
 * Como el paso ya se derivó del RANGO, preguntarle a él cuántos decimales
 * necesita da precisión donde el conjunto es apretado y números redondos donde
 * es ancho, sin ninguna regla aparte que mantener.
 */
export function escalaVertical(
  valores: number[],
): { dominio: [number, number]; ticks: number[]; decimales: number } | undefined {
  if (valores.length === 0) return undefined;

  let minimo = Infinity;
  let maximo = -Infinity;
  for (const valor of valores) {
    if (valor < minimo) minimo = valor;
    if (valor > maximo) maximo = valor;
  }

  // Con un solo valor —o con todos iguales— el alto es cero y no hay escala que
  // repartir: recharts deja el punto fuera del área o lo hace desaparecer. Un
  // décimo del propio valor le da una franja donde dibujarse.
  const alto = maximo - minimo || Math.max(Math.abs(maximo) * 0.1, 1);
  const aire = alto * 0.06;

  const piso = Math.max(minimo - aire, 0);
  const techo = maximo + aire;

  const paso = pasoLegible((techo - piso) / TRAMOS_DEL_EJE);
  const decimales = decimalesDe(paso);

  // `piso + i * paso` acumula error de punto flotante —0,1 + 0,2 no da 0,3— y
  // una marca de `1200.0000000000002` se imprime entera en cuanto el rótulo
  // pide decimales. Se redondea a la precisión del PASO, que es exactamente la
  // que la marca puede necesitar.
  const precision = decimales + 1;
  const ticks: number[] = [];
  // El tope es un cinturón contra un paso degenerado —un cero o un infinito por
  // un dato absurdo—, que sin él sería un bucle infinito y una pestaña colgada.
  // Con el paso derivado del rango nunca se alcanza.
  const TOPE = TRAMOS_DEL_EJE * 4;
  for (let n = Math.ceil(piso / paso); n * paso <= techo && ticks.length < TOPE; n += 1) {
    ticks.push(Number((n * paso).toFixed(precision)));
  }

  return { dominio: [piso, techo], ticks, decimales };
}

function agruparElMercado(mercado: PuntoDelCatalogo[]): PuntoDeMercado[] {
  // Las mismas dos condiciones que las fichas: sin valor unitario no hay dónde
  // ponerlo en el eje, y dibujarlo en cero inventaría un dato barato que no
  // existe — justo abajo, que es donde más llama la atención.
  const datos = mercado
    .filter((p) => p.uf_ha !== null && p.uf_ha > 0 && p.m2 !== null && p.m2 > 0)
    .map((p) => ({ lat: p.uf_ha as number, lng: (p.m2 as number) / 10_000 }));

  if (datos.length === 0) return [];

  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;
  for (const { lat, lng } of datos) {
    if (lng < minX) minX = lng;
    if (lng > maxX) maxX = lng;
    if (lat < minY) minY = lat;
    if (lat > maxY) maxY = lat;
  }

  // Con todos los puntos en el mismo valor el rango es cero, y dividir por él
  // manda todas las claves a NaN: la nube entera se colapsaría en un punto o
  // desaparecería. Un rango mínimo los deja en una celda, que es lo correcto.
  const anchoX = maxX - minX || 1;
  const anchoY = maxY - minY || 1;

  return agruparPorGrilla(
    datos,
    ({ lat, lng }) => ({
      x: ((lng - minX) / anchoX) * CELDAS_X,
      y: ((lat - minY) / anchoY) * CELDAS_Y,
    }),
    1,
  ).map((grupo) => ({ x: grupo.lng, y: grupo.lat, cantidad: grupo.cantidad }));
}

/**
 * Una celda de la nube: gris, tenue y detrás de todo.
 *
 * El radio crece con la RAÍZ de la cantidad porque lo que el ojo compara es el
 * ÁREA: con el radio proporcional al conteo, una celda de 100 se vería diez mil
 * veces más grande que una de 1.
 */
function PuntoDeLaNube(props: unknown) {
  const { cx, cy, cantidad } = props as { cx?: number; cy?: number; cantidad?: number };
  if (cx === undefined || cy === undefined) return null;

  const cuantos = cantidad ?? 1;

  return (
    <circle
      cx={cx}
      cy={cy}
      r={Math.min(2 + Math.sqrt(cuantos) * 0.9, 11)}
      fill="var(--color-ink-hint)"
      fillOpacity={cuantos > 1 ? 0.22 : 0.35}
      stroke="none"
    />
  );
}
